import { useEffect } from 'react';

interface SEOProps {
    title: string;
    description?: string;
    image?: string;
    url?: string;
    type?: 'website' | 'article';
    canonical?: string;
    jsonLd?: Record<string, any>;
}

const BASE_URL = 'https://starquant.ai';
const SUPPORTED_LANGS = ['en', 'es', 'pt', 'de', 'it', 'fr'];

/**
 * Normalise une URL en supprimant le trailing slash (sauf sur la homepage /).
 * Exemple : /glossary/scalping/ → /glossary/scalping
 */
const normalizeUrl = (url: string): string => {
    try {
        const u = new URL(url);
        if (u.pathname !== '/' && u.pathname.endsWith('/')) {
            u.pathname = u.pathname.slice(0, -1);
        }
        return u.toString();
    } catch {
        return url;
    }
};

/**
 * Extrait le chemin sans préfixe de langue.
 * Exemple: /en/glossary/scalping → /glossary/scalping
 *          /fr/blog/article → /blog/article (fr est la racine)
 *          /glossary/scalping → /glossary/scalping
 */
const getPathWithoutLang = (pathname: string): string => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length > 0 && SUPPORTED_LANGS.includes(parts[0])) {
        const rest = parts.slice(1).join('/');
        return rest ? `/${rest}` : '/';
    }
    return pathname || '/';
};

/**
 * Retourne le code de langue ISO depuis le pathname.
 */
const getLangFromPath = (pathname: string): string => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length > 0 && SUPPORTED_LANGS.includes(parts[0])) {
        return parts[0];
    }
    return 'fr'; // French is root (no prefix)
};

/**
 * Hook to dynamically update SEO tags (Title, Description, OpenGraph, Twitter, Canonical, JSON-LD, Hreflang).
 * Optimized for both classic SEO (Google) and Generative Engine Optimization (GEO).
 *
 * Key rules enforced:
 * - Canonical URLs NEVER have a trailing slash (except homepage root /)
 * - Hreflang tags are always reciprocal and include x-default
 * - og:locale is set for each language page
 * - All URLs in signals (canonical, hreflang, og:url) are normalized consistently
 */
export const useSEO = ({
    title,
    description,
    keywords,
    image = `${BASE_URL}/ascreen.jpg`,
    url,
    type = 'website',
    canonical,
    jsonLd,
    noindex = false
}: SEOProps & { keywords?: string; noindex?: boolean }) => {

    useEffect(() => {
        const siteName = 'StarQuant';
        const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;

        // --- 1. Compute the canonical URL (normalized, no trailing slash) ---
        const rawUrl = url || window.location.href;
        const rawCanonical = canonical || rawUrl.split('?')[0];
        let targetCanonical = normalizeUrl(rawCanonical);

        // French is at root, remove /fr prefix if present
        try {
            const urlObj = new URL(targetCanonical);
            if (urlObj.pathname.startsWith('/fr/')) {
                urlObj.pathname = urlObj.pathname.replace(/^\/fr/, '');
                targetCanonical = urlObj.toString();
            } else if (urlObj.pathname === '/fr') {
                urlObj.pathname = '/';
                targetCanonical = urlObj.toString();
            }
            // Final normalization (trailing slash)
            targetCanonical = normalizeUrl(targetCanonical);
        } catch { /* keep as-is */ }

        // --- 2. Compute clean path for hreflang ---
        const currentPathname = window.location.pathname;
        let cleanPath = getPathWithoutLang(currentPathname);
        // Normalize: remove trailing slash
        if (cleanPath !== '/' && cleanPath.endsWith('/')) {
            cleanPath = cleanPath.slice(0, -1);
        }

        const currentLang = getLangFromPath(currentPathname);

        // --- 3. og:locale mapping ---
        const ogLocaleMap: Record<string, string> = {
            fr: 'fr_FR',
            en: 'en_US',
            es: 'es_ES',
            pt: 'pt_PT',
            de: 'de_DE',
            it: 'it_IT',
        };

        // Helper: update or create a meta tag
        const updateMeta = (nameOrProperty: string, value: string) => {
            const selector = nameOrProperty.startsWith('og:') || nameOrProperty.startsWith('fb:') || nameOrProperty.startsWith('twitter:') && nameOrProperty !== 'twitter:card' && nameOrProperty !== 'twitter:site'
                ? `meta[property="${nameOrProperty}"], meta[name="${nameOrProperty}"]`
                : `meta[name="${nameOrProperty}"]`;
            let el = document.querySelector(selector) as HTMLMetaElement;
            if (el) {
                el.setAttribute('content', value);
            } else {
                const head = document.getElementsByTagName('head')[0];
                const meta = document.createElement('meta');
                if (nameOrProperty.startsWith('og:') || nameOrProperty.startsWith('fb:')) {
                    meta.setAttribute('property', nameOrProperty);
                } else {
                    meta.setAttribute('name', nameOrProperty);
                }
                meta.setAttribute('content', value);
                head.appendChild(meta);
            }
        };

        // --- Apply basic meta tags ---
        document.title = fullTitle;
        updateMeta('og:site_name', siteName);
        updateMeta('og:title', fullTitle);
        updateMeta('og:url', targetCanonical);
        updateMeta('og:type', type);
        updateMeta('og:image', image);
        updateMeta('og:image:width', '1200');
        updateMeta('og:image:height', '630');
        updateMeta('og:locale', ogLocaleMap[currentLang] || 'fr_FR');

        updateMeta('twitter:card', 'summary_large_image');
        updateMeta('twitter:title', fullTitle);
        updateMeta('twitter:image', image);
        updateMeta('twitter:site', '@starquant_ai');

        if (description) {
            updateMeta('description', description);
            updateMeta('og:description', description);
            updateMeta('twitter:description', description);
        }

        if (keywords) {
            updateMeta('keywords', keywords);
        }

        // robots directive
        if (noindex) {
            updateMeta('robots', 'noindex, nofollow');
        } else {
            updateMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
        }

        // --- 4. Canonical link tag ---
        let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
        if (linkCanonical) {
            linkCanonical.setAttribute('href', targetCanonical);
        } else {
            linkCanonical = document.createElement('link');
            linkCanonical.setAttribute('rel', 'canonical');
            linkCanonical.setAttribute('href', targetCanonical);
            document.head.appendChild(linkCanonical);
        }

        // --- 5. Hreflang alternate tags ---
        // Remove existing alternate tags first (cleanup)
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

        // Build the hreflang set for all supported languages
        SUPPORTED_LANGS.forEach(lang => {
            const alt = document.createElement('link');
            alt.rel = 'alternate';
            alt.hreflang = lang;

            // French is at root: /path | Other langs: /lang/path
            let href: string;
            if (lang === 'fr') {
                href = `${BASE_URL}${cleanPath === '/' ? '/' : cleanPath}`;
            } else {
                href = `${BASE_URL}/${lang}${cleanPath === '/' ? '' : cleanPath}`;
            }
            alt.href = href;
            document.head.appendChild(alt);

            // x-default → English (or French if no English)
            if (lang === 'en') {
                const xDefault = document.createElement('link');
                xDefault.rel = 'alternate';
                xDefault.hreflang = 'x-default';
                xDefault.href = href;
                document.head.appendChild(xDefault);
            }
        });

        // --- 6. JSON-LD Structured Data ---
        let scriptJsonLd = document.getElementById('json-ld-seo') as HTMLScriptElement;
        if (jsonLd) {
            const jsonLdStr = JSON.stringify(jsonLd);
            if (scriptJsonLd) {
                scriptJsonLd.text = jsonLdStr;
            } else {
                scriptJsonLd = document.createElement('script');
                scriptJsonLd.id = 'json-ld-seo';
                scriptJsonLd.type = 'application/ld+json';
                scriptJsonLd.text = jsonLdStr;
                document.head.appendChild(scriptJsonLd);
            }
        } else if (scriptJsonLd) {
            scriptJsonLd.remove();
        }

        return () => {
            document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
        };

    }, [title, description, keywords, image, url, type, canonical, jsonLd, noindex]);
};
